import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "antd";
import { CaretRightFilled, LoadingOutlined, PauseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { KitchenHoldOut } from "../../../../../api/billKitchenHold";
import { KitchenHoldTimeOptions } from "../../menu/KitchenHoldTimeOptions";
import type { KitchenHoldTime } from "../../menu/kitchenHoldItems";

interface KitchenHoldActionsProps {
  hold: KitchenHoldOut;
  /** „Промени" — отваря/затваря прозорчето с времената до бутона. */
  onToggleChange: () => void;
  onCloseChange: () => void;
  changeOpen: boolean;
  onPickTime: (time: KitchenHoldTime) => void;
  onRelease: () => void;
  /** Заявката за тази партида тече — коя (спинерът е на нейния бутон). */
  busyAction: "release" | "change" | null;
  /** Заявка за друга партида тече — една по една. */
  disabled: boolean;
  /** „Недостъпно без интернет" в локален режим (касата не сервира пътищата). */
  lockedTooltip?: string;
  /** Телефон: само иконките (имената — в подсказката и за екранния четец). */
  iconOnly: boolean;
}

// Двоен клик по бутоните не е повторна поръчка, кликът не избира ред.
const stopRowEvents = {
  onClick: (event: React.MouseEvent) => event.stopPropagation(),
  onDoubleClick: (event: React.MouseEvent) => event.stopPropagation(),
};

/** Ширината на прозорчето (седемте бутона 1:1 като в прегледа) и отстъпите. */
const PICKER_WIDTH = 316;
const PICKER_HEIGHT = 54;
const VIEWPORT_MARGIN = 8;
const BUTTON_GAP = 6;

/** Къде да застане прозорчето: под бутона, подравнено по десния му ръб, но
 *  винаги в екрана (в панела до „Схема" бутонът може да е вляво); над бутона,
 *  ако отдолу няма място. */
const pickerPosition = (button: HTMLElement) => {
  const rect = button.getBoundingClientRect();
  const width = Math.min(PICKER_WIDTH, window.innerWidth - 2 * VIEWPORT_MARGIN);
  const left = Math.min(
    Math.max(VIEWPORT_MARGIN, rect.right - width),
    window.innerWidth - width - VIEWPORT_MARGIN,
  );
  const fitsBelow = rect.bottom + BUTTON_GAP + PICKER_HEIGHT <= window.innerHeight - VIEWPORT_MARGIN;
  const top = fitsBelow ? rect.bottom + BUTTON_GAP : rect.top - BUTTON_GAP - PICKER_HEIGHT;
  return { top, left, width };
};

/**
 * „⏸ Промени" (рамка) и „▶ Пусни сега" (плътен акцент) вдясно в разделителя
 * на задържана партида; на телефон — само иконките, малки квадратни, на реда
 * на разделителя. „Промени"
 * отваря до себе си прозорче със същите седем бутона като в „Преглед на
 * поръчката" (`menu/KitchenHoldTimeOptions`, 1:1 по размер): отбелязан е ∞
 * при ръчно задържане, при таймер — нищо (срокът вляво казва точния час, а
 * изборът брои от сега). Прозорчето е в портал върху целия екран — списъкът
 * на масата и панелът до „Схема" са с `overflow`, който би го отрязал. Клик
 * извън него, Esc и смяна на размера на прозореца го затварят — Esc с
 * preventDefault, иначе минава и през глобалната навигация „назад".
 * Заключените действия са с подсказка на обвивката (disabled бутон не
 * получава mouse събития), както в „Опции".
 */
export const KitchenHoldActions = ({
  hold,
  onToggleChange,
  onCloseChange,
  changeOpen,
  onPickTime,
  onRelease,
  busyAction,
  disabled,
  lockedTooltip,
  iconOnly,
}: KitchenHoldActionsProps) => {
  const { t } = useTranslation();
  const inactive = busyAction !== null || disabled || !!lockedTooltip;
  const changeLabel = t("ordering.kitchenHold.change");
  const releaseLabel = t("ordering.kitchenHold.release");
  const changeButtonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<ReturnType<typeof pickerPosition> | null>(null);

  useLayoutEffect(() => {
    setPosition(changeOpen && changeButtonRef.current ? pickerPosition(changeButtonRef.current) : null);
  }, [changeOpen]);

  useEffect(() => {
    if (!changeOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onCloseChange();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onCloseChange);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onCloseChange);
    };
  }, [changeOpen, onCloseChange]);

  const spinnerSize = iconOnly ? "text-[12px]" : "text-[11px]";

  // Литерали, не сглобени класове — Tailwind ги търси в изходния код.
  // Телефон: малки квадратни, само иконката — на реда на разделителя.
  const shape = iconOnly ? "w-7 h-7 justify-center" : "h-7 gap-1 px-2";
  const buttons = (
    <span
      className={`inline-flex shrink-0 items-center ${iconOnly ? "gap-1" : "gap-1.5"} ${lockedTooltip ? "pointer-events-none" : ""}`}
    >
      <span className="inline-flex">
        <button
          ref={changeButtonRef}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggleChange();
          }}
          onDoubleClick={stopRowEvents.onDoubleClick}
          aria-expanded={changeOpen}
          aria-label={iconOnly ? changeLabel : undefined}
          title={iconOnly ? changeLabel : undefined}
          disabled={inactive}
          className={`inline-flex shrink-0 cursor-pointer items-center whitespace-nowrap rounded-lg border text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${shape} ${
            changeOpen
              ? "border-pos-accent bg-pos-marked text-pos-accent-ink"
              : "border-pos-ctl bg-pos-card text-pos-ink2 enabled:hover:bg-pos-panel"
          }`}
        >
          {busyAction === "change" ? (
            <LoadingOutlined className={spinnerSize} />
          ) : (
            <PauseCircleOutlined className={iconOnly ? "text-[13px]" : "text-[11px]"} />
          )}
          {!iconOnly && changeLabel}
        </button>
        {changeOpen &&
          position &&
          createPortal(
            <>
              {/* Backdrop — клик извън прозорчето го затваря */}
              <div
                className="fixed inset-0 z-[1000]"
                onClick={(event) => {
                  event.stopPropagation();
                  onCloseChange();
                }}
                onDoubleClick={stopRowEvents.onDoubleClick}
              />
              <div
                {...stopRowEvents}
                // Позицията е по бутона — известна чак по време на изпълнение.
                style={{ top: position.top, left: position.left, width: position.width }}
                className="dk-elevated fixed z-[1001] rounded-xl border border-pos-line bg-pos-card p-2 shadow-[0_16px_40px_rgba(60,40,20,.16)]"
              >
                <KitchenHoldTimeOptions
                  selected={hold.hold_until ? null : "manual"}
                  onPick={onPickTime}
                />
              </div>
            </>,
            document.body,
          )}
      </span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onRelease();
        }}
        onDoubleClick={stopRowEvents.onDoubleClick}
        aria-label={iconOnly ? releaseLabel : undefined}
        title={iconOnly ? releaseLabel : undefined}
        disabled={inactive}
        className={`inline-flex shrink-0 cursor-pointer items-center whitespace-nowrap rounded-lg border border-pos-accent bg-pos-accent text-[12px] font-semibold text-white transition-opacity enabled:hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-45 ${shape}`}
      >
        {busyAction === "release" ? (
          <LoadingOutlined className={spinnerSize} />
        ) : (
          <CaretRightFilled className={iconOnly ? "text-[13px]" : "text-[11px]"} />
        )}
        {!iconOnly && releaseLabel}
      </button>
    </span>
  );
  return lockedTooltip ? (
    <Tooltip title={lockedTooltip}>
      <span className="inline-flex shrink-0 cursor-not-allowed">{buttons}</span>
    </Tooltip>
  ) : (
    buttons
  );
};
